import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaShare, FaCalendarAlt, FaEye, FaTag, FaStar, FaPrint, FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp, FaInstagram, FaYoutube } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';
import { getImageUrl, getWeeklyHighlightImageUrl, safeJsonParse } from '../utils/imageUtils';
import ImagePlaceholder from './ImagePlaceholder';
import Header from './Header';
import Footer from './Footer';
import './WeeklyHighlightsPrint.css';

// Add CSS styles for React Quill content rendering
const quillStyles = `
  /* React Quill Size Classes - These are the actual classes Quill generates */
  .ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  .ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  /* Target elements with size classes */
  .ql-editor .ql-size-small,
  .card-content .ql-size-small,
  div .ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  .ql-editor .ql-size-large,
  .card-content .ql-size-large,
  div .ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .ql-editor .ql-size-huge,
  .card-content .ql-size-huge,
  div .ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  /* Target specific elements that might have size classes */
  .ql-editor p.ql-size-huge,
  .card-content p.ql-size-huge,
  div p.ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
    margin-bottom: 1.5rem !important;
  }
  
  .ql-editor p.ql-size-large,
  .card-content p.ql-size-large,
  div p.ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
    margin-bottom: 1.25rem !important;
  }
  
  .ql-editor p.ql-size-small,
  .card-content p.ql-size-small,
  div p.ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
    margin-bottom: 0.75rem !important;
  }
  
  /* Target headings with size classes */
  .ql-editor h1.ql-size-huge,
  .ql-editor h2.ql-size-huge,
  .ql-editor h3.ql-size-huge,
  .card-content h1.ql-size-huge,
  .card-content h2.ql-size-huge,
  .card-content h3.ql-size-huge,
  div h1.ql-size-huge,
  div h2.ql-size-huge,
  div h3.ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  .ql-editor h1.ql-size-large,
  .ql-editor h2.ql-size-large,
  .ql-editor h3.ql-size-large,
  .card-content h1.ql-size-large,
  .card-content h2.ql-size-large,
  .card-content h3.ql-size-large,
  div h1.ql-size-large,
  div h2.ql-size-large,
  div h3.ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .ql-editor h1.ql-size-small,
  .ql-editor h2.ql-size-small,
  .ql-editor h3.ql-size-small,
  .card-content h1.ql-size-small,
  .card-content h2.ql-size-small,
  .card-content h3.ql-size-small,
  div h1.ql-size-small,
  div h2.ql-size-small,
  div h3.ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  /* Universal selector to catch any element with size classes */
  *[class*="ql-size-huge"] {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  *[class*="ql-size-large"] {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  *[class*="ql-size-small"] {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  /* Sidebar sticky positioning fix */
  .weekly-highlights-sidebar {
    position: sticky !important;
    top: 7rem !important;
    z-index: 999 !important;
    max-height: calc(100vh - 8rem) !important;
    overflow-y: auto !important;
    scrollbar-width: thin !important;
    scrollbar-color: #cbd5e1 #f1f5f9 !important;
  }
  
  /* Custom scrollbar styling for webkit browsers */
  .weekly-highlights-sidebar::-webkit-scrollbar {
    width: 6px !important;
  }
  
  .weekly-highlights-sidebar::-webkit-scrollbar-track {
    background: #f1f5f9 !important;
    border-radius: 3px !important;
  }
  
  .weekly-highlights-sidebar::-webkit-scrollbar-thumb {
    background: #cbd5e1 !important;
    border-radius: 3px !important;
  }
  
  .weekly-highlights-sidebar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8 !important;
  }
  
  /* Ensure sidebar doesn't overlap with header */
  @media (min-width: 992px) {
    .weekly-highlights-sidebar {
      top: 7rem !important;
      max-height: calc(100vh - 8rem) !important;
    }
  }
  
  /* Mobile responsive sidebar positioning */
  @media (max-width: 991px) {
    .weekly-highlights-sidebar {
      position: static !important;
      top: auto !important;
      max-height: none !important;
      overflow-y: visible !important;
    }
  }
  
  /* Extra small screens */
  @media (max-width: 576px) {
    .weekly-highlights-sidebar {
      margin-top: 0 !important;
    }
  }

  /* Mobile-only adjustments scoped to Weekly Highlight page */
  @media (max-width: 576px) {
    .weekly-highlights-page .card-body.p-4 {
      padding: 0.5rem !important; /* Bootstrap p-2 equivalent */
    }
    .weekly-highlights-page .wb-pad-24 {
      padding: 14px !important; /* Override 24px to 14px */
    }
    .weekly-highlights-page .card-content {
      padding: 5px !important; /* Override 20px to 5px */
    }
    /* Target elements with inline padding: 20px specifically */
    .weekly-highlights-page div[style*="padding: 20px"] {
      padding: 5px !important;
    }
    .weekly-highlights-page div[style*="padding: '20px'"] {
      padding: 5px !important;
    }
    /* Reduce list padding on mobile */
    .weekly-highlights-page ol, .weekly-highlights-page ul {
      padding-left: 1rem !important;
    }
    /* Add margin-top to h2 elements on mobile */
    .weekly-highlights-page h2.h3.fw-bold.mb-4 {
      margin-top: 0.5rem !important; /* Bootstrap mt-2 equivalent */
    }
  }

  /* Desktop-only adjustments for related highlight cards */
  @media (min-width: 577px) {
    .weekly-highlights-page .related-articles div[style*="height: 310px"] {
      height: 100% !important;
    }
    .weekly-highlights-page .related-articles div[style*="margin-top: auto"] {
      margin-top: 1rem !important;
    }
  }

  /* Remove scrollbar from sidebar only */
  .weekly-highlights-page .weekly-highlights-sidebar {
    overflow: hidden !important;
  }

  /* Change weekly cards height to 100% */
  .weekly-highlights-page .related-articles div[style*="height: 310px"] {
    height: 100% !important;
  }

  /* Change button container margin-top from auto to 1rem */
  .weekly-highlights-page .related-articles div[style*="margin-top: auto"] {
    margin-top: 1rem !important;
  }
`;

const WeeklyHighlightDetail = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const [highlight, setHighlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedHighlights, setRelatedHighlights] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    fetchHighlight();
  }, [identifier]);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300); // Show button when scrolled more than 300px
    };

    window.addEventListener('scroll', handleScroll);
    
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const fetchHighlight = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.WEEKLY_HIGHLIGHT_PUBLIC(identifier));
      
      // Parse JSON fields from the database using safe parsing
      const highlightData = {
        ...response.data,
        highlights: safeJsonParse(response.data.highlights, []),
        tags: safeJsonParse(response.data.tags, []),
        detail_cards: safeJsonParse(response.data.detail_cards, [])
      };
      
      setHighlight(highlightData);
      
      // Fetch related highlights
      const relatedResponse = await axios.get(API_ENDPOINTS.WEEKLY_HIGHLIGHTS_PUBLIC);
      
      const allHighlights = relatedResponse.data
        .filter(b => b.id !== highlightData.id && b.status === 'published')
        .map(b => ({
          ...b,
          highlights: safeJsonParse(b.highlights, []),
          tags: safeJsonParse(b.tags, [])
        }));
      
      setRelatedHighlights(allHighlights.slice(0, 3));
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch highlight:', err);
      setError(err.response?.data?.error || 'Failed to fetch highlight');
      setLoading(false);
    }
  };

  const formatWeekLabel = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'long', day: 'numeric', year: 'numeric' };
    const startDate = new Date(date);
    startDate.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
    
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6); // End of week (Saturday)
    
    return `Week of ${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  const formatCoveragePeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const getDayWithSuffix = (day) => {
      if (day >= 11 && day <= 13) return day + 'th';
      switch (day % 10) {
        case 1: return day + 'st';
        case 2: return day + 'nd';
        case 3: return day + 'rd';
        default: return day + 'th';
      }
    };
    
    const startDay = getDayWithSuffix(start.getDate());
    const endDay = getDayWithSuffix(end.getDate());
    const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
    const year = start.getFullYear();
    
    if (start.getMonth() === end.getMonth()) {
      return `${startDay} - ${endDay} ${startMonth} ${year}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
    }
  };

  const handleShare = async (platform) => {
    const url = window.location.href;
    const title = highlight.title;
    const text = highlight.subtitle || highlight.meta_description;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`;
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({
              title: title,
              text: text,
              url: url
            });
            return;
          } catch (err) {
            console.log('Share cancelled');
          }
        }
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading highlight...</p>
        </div>
      </div>
    );
  }

  if (error || !highlight) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">❌ Error Loading Highlight</h4>
          <p>{error || 'Highlight not found'}</p>
          <button 
            className="btn btn-outline-danger"
            onClick={() => navigate('/weekly-highlights')}
          >
            Back to All Highlights
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Add React Quill styles for proper font rendering */}
      <style dangerouslySetInnerHTML={{ __html: quillStyles }} />
      
    <div className="weekly-highlights-page" style={{ 
      background: 'linear-gradient(to right, #91eae4, #86a8e7, #7f7fd5)',
      minHeight: '100vh',
      fontFamily: '"Quicksand", sans-serif'
    }}>
      {/* Header Navigation */}
      <Header />
      
      {/* Back to All Highlights Button - Above Hero Card */}
      <div className="container mb-4" style={{ paddingTop: '1.5rem' }}>
        <div className="d-flex justify-content-start">
          <button
            className="btn btn-primary btn-lg"
            onClick={() => navigate('/weekly-highlights')}
            style={{
              background: 'linear-gradient(135deg, #7f7fd5, #86a8e7)',
              border: 'none',
              borderRadius: '16px',
              padding: '0.875rem 1.75rem',
              fontWeight: '600',
              fontSize: '1rem',
              boxShadow: '0 6px 20px rgba(127, 127, 213, 0.25)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(127, 127, 213, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 6px 20px rgba(127, 127, 213, 0.25)';
            }}
          >
            <FaArrowLeft style={{ marginRight: '0.75rem', fontSize: '1.1rem' }} />
            Back to All Highlights
          </button>
        </div>
      </div>

      {/* Hero Card */}
      <div className="container mb-4">
        <div className="card" style={{
          border: 'none',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 25px 50px rgba(0,0,0,0.12)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
        }}>
                     {/* Cover Image */}
           <div style={{ position: 'relative', height: '450px', overflow: 'hidden' }}>
             {highlight.cover_image ? (
               <img
                 src={getWeeklyHighlightImageUrl(highlight.cover_image)}
                 alt={highlight.title}
                 style={{
                   width: '100%',
                   height: '100%',
                   objectFit: 'cover',
                   display: 'block'
                 }}
               />
             ) : (
               <img
                 src={getWeeklyHighlightImageUrl(highlight.cover_image)}
                 alt={highlight.title || "Weekly Highlight"}
                 style={{
                   width: '100%',
                   height: '100%',
                   objectFit: 'cover',
                   display: 'block'
                 }}
               />
             )}
            
            {/* Overlay Gradient */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '60%',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))'
            }}></div>
            
            {/* Featured Badge */}
            {highlight.featured && (
              <div style={{
                position: 'absolute',
                top: '1.5rem',
                left: '1.5rem',
                background: 'linear-gradient(135deg, #ffd700, #ffed4e)',
                color: '#1f2937',
                padding: '0.75rem 1.5rem',
                borderRadius: '25px',
                fontSize: '0.875rem',
                fontWeight: '700',
                boxShadow: '0 8px 20px rgba(255, 215, 0, 0.4)'
              }}>
                <FaStar style={{ marginRight: '0.5rem' }} />
                FEATURED BULLETIN
              </div>
            )}


          </div>
          
          {/* Hero Content */}
          <div className="card-body p-4" style={{ position: 'relative' }}>
            <div className="row align-items-start">
              <div className="col-lg-8">
                <h1 className="display-6 fw-bold mb-4" style={{ 
                  color: '#1f2937',
                  lineHeight: '1.2',
                  letterSpacing: '-0.025em'
                }}>
                  {highlight.title}
                </h1>
                
                {/* Coverage Period Section */}
                  {highlight.coverage_period_start && highlight.coverage_period_end && (
                  <div className="mb-4" style={{ marginTop: '2rem' }}>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        {/* Date Range Badge */}
                          <span className="badge" style={{ 
                            background: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
                            color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        padding: '0.8rem 1rem',
                        borderRadius: '12px',
                        boxShadow: '0 4px 15px rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(255, 255, 255, 0.15)'
                      }}>
                        <FaCalendarAlt style={{ marginRight: '0.25rem', fontSize: '0.75rem' }} />
                            {formatCoveragePeriod(highlight.coverage_period_start, highlight.coverage_period_end)}
                          </span>
                        
                      {/* Weekly Edition Badge */}
                            {highlight.weekly_edition && (
                              <span className="badge" style={{ 
                                background: 'linear-gradient(135deg, #10b981, #34d399)',
                                color: 'white',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                          padding: '0.8rem 1rem',
                          borderRadius: '12px',
                                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.2)',
                                border: '1px solid rgba(255, 255, 255, 0.15)'
                              }}>
                                {highlight.weekly_edition} Weekly Edition
                              </span>
                            )}
                            
                      {/* Coverage Month Badge */}
                            {highlight.coverage_month && (
                              <span className="badge" style={{ 
                                background: 'linear-gradient(135deg, #06b6d4, #22d3ee)',
                                color: 'white',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                          padding: '0.8rem 1rem',
                          borderRadius: '12px',
                                boxShadow: '0 4px 15px rgba(6, 182, 212, 0.2)',
                                border: '1px solid rgba(255, 255, 255, 0.15)'
                              }}>
                                {highlight.coverage_month} {new Date(highlight.coverage_period_start || highlight.issue_date).getFullYear()}
                              </span>
                            )}
                    </div>
                          </div>
                        )}
                
                {highlight.subtitle && (
                  <p className="lead text-muted mb-4" style={{ 
                    fontSize: '1.125rem', 
                    lineHeight: '1.6',
                    fontWeight: '400'
                  }}>
                    {highlight.subtitle}
                  </p>
                        )}
                      </div>
              

                    </div>
                </div>
              </div>
      </div>



      {/* Highlights Card */}
      {highlight.highlights && Array.isArray(highlight.highlights) && highlight.highlights.length > 0 && (
      <div className="container mb-4">
            <div className="card" style={{
              border: 'none',
              borderRadius: '20px',
              boxShadow: '0 12px 35px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            borderLeft: '4px solid #ffd700'
            }}>
              <div className="card-body p-4">
                    <h2 className="h3 fw-bold mb-4" style={{ color: '#1f2937' }}>
                      <FaStar style={{ color: '#ffd700', marginRight: '0.5rem' }} />
                      Highlights of the Week
                    </h2>
                    
                    {highlight.highlights.map((highlight, index) => (
                      <div key={index} className="mb-4">
                        {highlight.title && (
                          <h4 className="h5 fw-semibold mb-3" style={{ color: '#374151' }}>
                            {highlight.title}
                          </h4>
                        )}
                        
                        {highlight.points && Array.isArray(highlight.points) && highlight.points.length > 0 && (
                          <ul className="list-unstyled">
                            {highlight.points.map((point, pointIndex) => (
                              <li key={pointIndex} className="mb-2 d-flex align-items-start">
                                <span style={{
                                  color: '#7f7fd5',
                                  marginRight: '0.75rem',
                                  fontSize: '1.2rem',
                                  lineHeight: '1'
                                }}>•</span>
                                <span style={{ color: '#4b5563', lineHeight: '1.6' }}>
                                  {point}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
            </div>
          </div>
                  </div>
                )}
                
      {/* Main Content */}
      <div className="container mb-4">
        <div className="row g-4">
          {/* Left Column - Main Content */}
          <div className="col-lg-9">
            <div className="card" style={{
              border: 'none',
              borderRadius: '20px',
              boxShadow: '0 12px 35px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <div className="card-body p-4">
                {/* Full Detail Content */}
                {(highlight.detail_cards && highlight.detail_cards.length > 0) || (highlight.full_detail && typeof highlight.full_detail === 'string') ? (
                  <div>
                    <h2 className="h3 fw-bold mb-5" style={{ 
                      color: '#1f2937',
                      fontSize: '1.75rem',
                      borderBottom: '3px solid #3b82f6',
                      paddingBottom: '12px',
                      display: 'inline-block'
                    }}>
                      Full Highlight Details
                    </h2>
                    
                    {/* Detail Cards */}
                    {highlight.detail_cards && highlight.detail_cards.length > 0 && (
                      <div className="mb-4">
                        {highlight.detail_cards.map((card, index) => (
                          <div key={card.id || index} className="mb-5" style={{
                            background: '#ffffff',
                            borderRadius: '12px',
                            padding: '0',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                            overflow: 'hidden',
                            border: '1px solid #e2e8f0'
                          }}>
                                                        {/* Card Title (Header) - Dark Blue Background */}
                            {card.title && (
                              <div style={{
                                background: '#1e3a8a',
                                padding: '30px',
                                marginBottom: '0',
                                borderRadius: '12px 12px 0 0'
                              }}>
                                <h1 className="h1 fw-bold mb-2 text-white" style={{ 
                                  fontSize: '1.8rem',
                                  lineHeight: '1.3',
                                  marginBottom: '8px',
                                  margin: '0',
                                  fontWeight: '600'
                                }}>
                                  {card.title}
                                </h1>
                                {/* White Separator Line - Shorter */}
                                <div style={{
                                  height: '1px',
                                  background: '#ffffff',
                                  width: '20%',
                                  marginTop: '16px',
                                  marginLeft: '0'
                                }}></div>
                              </div>
                            )}
                            
                            {/* Card Content Container */}
                            <div className="wb-pad-24" style={{ padding: '24px' }}>
                                                          {/* Card Subtitle (Bright Blue Bar) */}
                            {card.subtitle && (
                              <div style={{
                                position: 'relative',
                                marginBottom: '20px',
                                background: '#3b82f6',
                                padding: '12px 20px 12px 20px',
                                borderRadius: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)'
                              }}>
                                {/* Content Text */}
                                <h3 className="h3 fw-bold mb-0" style={{ 
                                  color: '#ffffff',
                                  fontSize: '1.3rem',
                                  lineHeight: '1.4',
                                  margin: '0',
                                  flex: '1'
                                }}>
                                  {card.subtitle}
                                </h3>
                              </div>
                            )}
                              
                              {/* Card Content */}
                              {card.content && (
                                <div 
                                  className="card-content"
                                  style={{
                                    padding: '20px',
                                    color: '#334155',
                      lineHeight: '1.8',
                                    fontSize: '1rem',
                                    background: '#ffffff',
                                    borderRadius: '8px',
                                    marginTop: '16px'
                                  }}
                                  dangerouslySetInnerHTML={{ __html: card.content }}
                                />
                              )}
                              
                              {/* Reference Links */}
                              {card.referenceLinks && card.referenceLinks.length > 0 && (
                                <div style={{
                                  padding: '20px',
                                  background: '#ffffff',
                                  borderRadius: '8px',
                                  marginTop: '16px',
                                  border: '1px solid #e2e8f0'
                                }}>
                                  <h6 className="fw-semibold mb-3" style={{ 
                                    color: '#475569',
                                    fontSize: '0.875rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                  }}>
                                    Reference Links:
                                  </h6>
                                  <div className="d-flex flex-column gap-2">
                                    {card.referenceLinks.map((link, linkIndex) => (
                                      <a 
                                        key={linkIndex}
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-none d-flex align-items-start"
                                        style={{ 
                                          color: '#3b82f6',
                                          fontSize: '0.875rem',
                                          padding: '12px',
                                          borderRadius: '8px',
                                          background: '#f8fafc',
                                          border: '1px solid #e2e8f0',
                                          transition: 'all 0.2s ease',
                                          wordBreak: 'break-all',
                                          wordWrap: 'break-word',
                                          overflowWrap: 'break-word',
                                          hyphens: 'auto'
                                        }}
                                        onMouseOver={(e) => {
                                          e.target.style.background = '#f1f5f9';
                                          e.target.style.borderColor = '#3b82f6';
                                        }}
                                        onMouseOut={(e) => {
                                          e.target.style.background = '#f8fafc';
                                          e.target.style.borderColor = '#e2e8f0';
                                        }}
                                      >
                                        <span style={{ 
                                          marginRight: '8px', 
                                          marginTop: '2px',
                                          flexShrink: 0
                                        }}>🔗</span>
                                        <span style={{
                                          wordBreak: 'break-all',
                                          wordWrap: 'break-word',
                                          overflowWrap: 'break-word',
                                          hyphens: 'auto',
                                          lineHeight: '1.4'
                                        }}>
                                          {link}
                                        </span>
                                      </a>
                      ))}
                    </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                  </div>
                )}
                    
                    {/* Legacy Full Detail (fallback) */}
                    {highlight.full_detail && typeof highlight.full_detail === 'string' && (!highlight.detail_cards || highlight.detail_cards.length === 0) && (
                      <div 
                        className="card-content"
                        style={{
                          color: '#374151',
                          lineHeight: '1.8',
                          fontSize: '1.1rem',
                          padding: '20px',
                          background: '#ffffff',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}
                        dangerouslySetInnerHTML={{ __html: highlight.full_detail }}
                      />
                    )}
                  </div>
                ) : null}
                
                {/* Tags */}
                {highlight.tags && Array.isArray(highlight.tags) && highlight.tags.length > 0 && (
                  <div className="mt-5 pt-4" style={{ borderTop: '1px solid #e5e7eb' }}>
                    <h5 className="fw-semibold mb-3" style={{ color: '#374151' }}>
                      <FaTag style={{ marginRight: '0.5rem' }} />
                      Tags
                    </h5>
                    <div className="d-flex flex-wrap gap-2">
                      {highlight.tags.map((tag, index) => (
                        <span key={index} style={{
                          background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
                          color: '#374151',
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          border: '1px solid #d1d5db'
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Right Column - Sidebar */}
          <div className="col-lg-3">
            <div className="weekly-highlights-sidebar">
              {/* Highlight Stats */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '16px',
                padding: '1.5rem',
                marginTop: '1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                animation: 'slideInRight 0.8s ease 0.2s both',
                border: '1px solid #e2e8f0'
              }}>
                <FaCalendarAlt style={{ color: '#64748b', fontSize: '1.2rem' }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: '#64748b', fontWeight: '500', fontSize: '0.875rem' }}>
                    Published on
                  </span>
                  <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '1rem' }}>
                    {highlight?.issue_date ? new Date(highlight.issue_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'No date'}
                  </span>
                </div>
              </div>
              
              {/* Social Share */}
              <div className="card mb-4" style={{
                border: 'none',
                borderRadius: '16px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
              }}>
                <div className="card-body p-4">
                  <h5 className="fw-bold mb-3" style={{ color: '#1f2937' }}>
                    Show some love!
                  </h5>
                  
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <a 
                      href="https://www.instagram.com/umoor.iqtesadiyah?igshid=MzRlODBiNWFlZA%3D%3D"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        width: '45px',
                        height: '45px',
                        borderRadius: '12px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #833AB4, #FD1D1D)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        textDecoration: 'none',
                        boxShadow: '0 4px 15px rgba(131, 58, 180, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.1) rotate(5deg)';
                        e.target.style.boxShadow = '0 8px 25px rgba(131, 58, 180, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1) rotate(0deg)';
                        e.target.style.boxShadow = '0 4px 15px rgba(131, 58, 180, 0.3)';
                      }}
                    >
                      <FaInstagram />
                    </a>
                    <a 
                      href="https://x.com/umooriqtesadiya?t=aPfr1rQc-pjdPWxZbUEH5g&s=31"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        width: '45px',
                        height: '45px',
                        borderRadius: '12px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #000000, #333333)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        textDecoration: 'none',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.1) rotate(-5deg)';
                        e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1) rotate(0deg)';
                        e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.3)';
                      }}
                    >
                      <div style={{ 
                        fontSize: '20px', 
                        fontWeight: 'bold',
                        color: 'white',
                        lineHeight: '1'
                      }}>
                        𝕏
                      </div>
                    </a>
                    <a 
                      href="https://www.youtube.com/@UmoorIqtesadiyah?si=Z36O3_WgkJuJHt1D"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        width: '45px',
                        height: '45px',
                        borderRadius: '12px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #FF0000, #CC0000)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        textDecoration: 'none',
                        boxShadow: '0 4px 15px rgba(255, 0, 0, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.1) rotate(5deg)';
                        e.target.style.boxShadow = '0 8px 25px rgba(255, 0, 0, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1) rotate(0deg)';
                        e.target.style.boxShadow = '0 4px 15px rgba(255, 0, 0, 0.3)';
                      }}
                    >
                      <FaYoutube />
                    </a>
                  </div>
                </div>
              </div>
              

              
              {/* Related Highlight Articles */}
              <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: '2rem',
                marginBottom: '1.5rem',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)',
                animation: 'slideInRight 0.8s ease 0.8s both',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <h4 style={{ 
                  fontSize: '1.2rem', 
                  fontWeight: '700', 
                  color: '#1f2937',
                  marginBottom: '1.5rem',
                  textAlign: 'center',
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  Related Highlight Articles
                </h4>
                

                
                {relatedHighlights.length > 0 ? (
                  <div className="related-articles" style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '1.25rem'
                  }}>
                    {relatedHighlights.map((relatedHighlight) => (
                      <div
                        key={relatedHighlight.id}
                        style={{
                          background: 'white',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          border: '1px solid #e2e8f0',
                          height: '310px',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                        }}
                        onClick={() => navigate(`/weekly-highlights/${relatedHighlight.custom_url || relatedHighlight.id}`)}
                      >
                        {/* Highlight Image */}
                        <div style={{ 
                          position: 'relative', 
                          width: '100%', 
                          height: '140px !important',
                          minHeight: '140px !important',
                          maxHeight: '140px !important',
                          flexShrink: 0
                        }}>
                          {relatedHighlight.cover_image ? (
                            <img
                              src={getWeeklyHighlightImageUrl(relatedHighlight.cover_image)}
                              alt={relatedHighlight.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                            />
                          ) : (
                            <ImagePlaceholder 
                              width="100%" 
                              height="100%" 
                              text="📰"
                              fontSize="1.5rem"
                            />
                          )}
                        </div>
                        
                        {/* Highlight Content */}
                        <div style={{ 
                          padding: '1rem',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}>
                          {/* Title Section */}
                          <div style={{ flex: 1 }}>
                            <h5 style={{
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              color: '#1f2937',
                              margin: '0 0 0.75rem 0',
                              lineHeight: '1.3',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minHeight: '2.6rem'
                            }}>
                              {relatedHighlight.title}
                            </h5>
                            
                            <small style={{
                              color: '#6b7280',
                              fontSize: '0.8rem',
                              fontWeight: '500'
                            }}>
                              {formatWeekLabel(relatedHighlight.issue_date)}
                            </small>
                          </div>
                          
                          {/* Read Highlight Button */}
                          <div style={{ marginTop: 'auto' }}>
                            <button
                              style={{
                                width: '100%',
                                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.75rem',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                              }}
                            >
                              Read Highlight
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem 1rem',
                    color: '#6b7280'
                  }}>
                    <div style={{
                      fontSize: '3rem',
                      marginBottom: '1rem',
                      opacity: '0.5'
                    }}>
                      📭
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}>
                      No other highlight articles available at the moment.
                    </p>
                    <p style={{
                      margin: '0.5rem 0 0 0',
                      fontSize: '0.8rem',
                      opacity: '0.7'
                    }}>
                      Check back later for more content in this category.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div style={{
        position: 'fixed',
        right: '2rem',
        bottom: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        zIndex: 999
      }}>
        <button
          title="Bookmark this article"
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: '2px solid #ff6b6b',
            background: 'rgba(255, 255, 255, 0.9)',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: '0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            boxShadow: 'rgba(0, 0, 0, 0.15) 0px 4px 20px',
            backdropFilter: 'blur(10px)',
            transform: 'scale(1) rotate(0deg)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1) rotate(5deg)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) rotate(0deg)';
          }}
        >
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#ff6b6b',
            lineHeight: '1'
          }}>
            🔖
          </div>
        </button>
        
        <button
          title="Print this article"
          onClick={handlePrint}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: '2px solid #3b82f6',
            background: 'rgba(255, 255, 255, 0.9)',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: '0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            boxShadow: 'rgba(0, 0, 0, 0.15) 0px 4px 20px',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
          }}
        >
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#3b82f6',
            lineHeight: '1'
          }}>
            🖨️
          </div>
        </button>
        
        <button
          title="Share this article"
          onClick={() => handleShare()}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: '2px solid #10b981',
            background: 'rgba(255, 255, 255, 0.9)',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: '0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            boxShadow: 'rgba(0, 0, 0, 0.15) 0px 4px 20px',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
          }}
        >
          <div style={{
            fontSize: '20px',
            color: '#10b981',
            lineHeight: '1'
          }}>
            📤
          </div>
        </button>
        
        {showScrollTop && (
        <button
          title="Scroll to top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: '2px solid #f59e0b',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: '0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            boxShadow: 'rgba(0, 0, 0, 0.15) 0px 4px 20px',
              transform: 'scale(1) translateY(0px)',
              opacity: showScrollTop ? 1 : 0,
              visibility: showScrollTop ? 'visible' : 'hidden'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1) translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) translateY(0px)';
          }}
        >
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: 'white',
            lineHeight: '1'
          }}>
            ⬆️
          </div>
        </button>
        )}
      </div>

      {/* Footer Spacing */}
      <div style={{ paddingBottom: '3rem' }}></div>
      
              {/* Footer */}
        <Footer />
    </div>
    </>
  );
};

export default WeeklyHighlightDetail;
